import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import {
    BackButton,
    BanknoteArrowDownIcon,
    BanknoteArrowUpIcon,
    FileTextIcon,
    isWeb,
    SafeAreaView,
    ScrollView,
    Spinner,
    TileButton,
    XStack,
    YStack,
} from 'smoothie';

import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureMessage } from '../../../logger/Logger';
import useGetAllBankAccountDetailsLegacy from '../../hooks/useGetAllBankAccountDetailsLegacy';
import { selectedSpaceAtom } from '../../state/SpacesAtoms';
import { preselectedTransferAccountsAtom } from '../../state/TransferAtoms';
import LegacySpaceCard, {
    SPACE_CARD_HORIZONTAL_MARGIN,
} from './LegacySpaceCard';
import LegacyTransactions from './transactions/LegacyTransactions';

const LegacySpaceDetails = () => {
    LogRocketFacade.tagPage('LegacySpaceDetails');

    const { syncteraAccountId } = useLocalSearchParams<{
        syncteraAccountId: string;
    }>();

    const setPreselectedTransferAccounts = useSetAtom(
        preselectedTransferAccountsAtom,
    );

    // Use global state to track selected space from here (used in account + transaction details modals)
    const [selectedSpace, setSelectedSpace] = useAtom(selectedSpaceAtom);

    const { bankAccountDetails, isLoading } =
        useGetAllBankAccountDetailsLegacy();

    useEffect(() => {
        if (!isLoading) {
            const account = bankAccountDetails.find(
                (acc) => acc.syncteraAccountId === syncteraAccountId,
            );

            if (account) {
                setSelectedSpace((prevAccount) => {
                    if (
                        prevAccount?.syncteraAccountId !==
                        account.syncteraAccountId
                    ) {
                        return account;
                    }
                    return prevAccount;
                });
            } else if (account == null && bankAccountDetails.length) {
                captureMessage('Space not found for details page');
                router.navigate('/home/banking');
            }
        }
    }, [syncteraAccountId, JSON.stringify(bankAccountDetails), isLoading]);

    const [accountScrollViewMaxWidth, setAccountScrollViewMaxWidth] =
        React.useState<number | null>(null);
    const updateAccountScrollingRequirement = () => {
        if (isWeb && bankAccountDetails.length > 0) {
            const contentWidth = bankAccountDetails.length * 250;
            const maxWidth =
                contentWidth < window.innerWidth ? contentWidth : null;
            setAccountScrollViewMaxWidth(maxWidth);
        }
    };
    useEffect(() => {
        updateAccountScrollingRequirement();
    }, [bankAccountDetails.length, selectedSpace]);

    const scrollViewRef = useRef(null);

    if (
        syncteraAccountId == null ||
        (bankAccountDetails == null && !isLoading)
    ) {
        captureMessage('Space not found for details page');
        return <Redirect href="/home/banking" />;
    }

    return (
        <SafeAreaView style={{ flex: 1, width: '100%' }}>
            <YStack flex={1} marginTop={24} paddingHorizontal={16} width="100%">
                <XStack alignItems="center" justifyContent="space-between">
                    <BackButton
                        background="transparent"
                        customBackFn={() => {
                            router.navigate('/home/banking');
                        }}
                    />
                </XStack>
                {isLoading ? (
                    <Spinner height={180} />
                ) : (
                    <ScrollView
                        marginHorizontal={-16}
                        paddingVertical={24}
                        horizontal={true}
                        showsHorizontalScrollIndicator={false}
                        ref={scrollViewRef}
                        maxHeight={180} // This is okay to hardcode since the SpaceCards are a fixed height
                        $sm={{
                            height: 180,
                            marginHorizontal: 40,
                            alignSelf:
                                accountScrollViewMaxWidth === null
                                    ? 'unset'
                                    : 'center',
                            maxWidth: accountScrollViewMaxWidth ?? 'unset',
                        }}
                        onLayout={updateAccountScrollingRequirement}
                    >
                        <XStack gap={8} flex={1}>
                            {bankAccountDetails.map((detail, index) => {
                                const isFirst = index === 0;
                                const isLast =
                                    index === bankAccountDetails.length - 1;

                                const isSelected =
                                    detail.bankAccountId ===
                                    selectedSpace?.bankAccountId;

                                return (
                                    <LegacySpaceCard
                                        key={detail.bankAccountId}
                                        size="sm"
                                        bankAccountDetail={detail}
                                        isSelected={isSelected}
                                        onPressToScrollTo={(
                                            x: number,
                                            y: number,
                                        ) => {
                                            setSelectedSpace(detail);
                                            // TODO: Figure out ref typing
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-expect-error
                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                            scrollViewRef?.current?.scrollTo({
                                                x,
                                                y,
                                                animated: true,
                                            });
                                        }}
                                        {...(isFirst
                                            ? {
                                                  marginLeft:
                                                      SPACE_CARD_HORIZONTAL_MARGIN,
                                              }
                                            : {})}
                                        {...(isLast
                                            ? {
                                                  marginRight:
                                                      SPACE_CARD_HORIZONTAL_MARGIN,
                                              }
                                            : {})}
                                        {...(!isSelected
                                            ? { opacity: 0.5 }
                                            : {})}
                                    />
                                );
                            })}
                        </XStack>
                    </ScrollView>
                )}
                <YStack
                    flex={1}
                    width="100%"
                    maxWidth={700}
                    alignSelf="center"
                    $sm={{
                        paddingTop: 8,
                        paddingHorizontal: 40,
                        paddingBottom: 16,
                    }}
                    $platform-web={{ maxHeight: 'calc(100vh - 300px)' }}
                >
                    <XStack
                        justifyContent="space-between"
                        gap={8}
                        marginBottom={40}
                        height={54}
                    >
                        <TileButton
                            icon={BanknoteArrowDownIcon}
                            text="Deposit"
                            onPress={() => {
                                setPreselectedTransferAccounts({
                                    toSyncteraAccountId:
                                        selectedSpace?.syncteraAccountId,
                                });
                                router.navigate('/banking/transfer');
                            }}
                        />
                        <TileButton
                            icon={BanknoteArrowUpIcon}
                            text="Withdraw"
                            onPress={() => {
                                setPreselectedTransferAccounts({
                                    fromSyncteraAccountId:
                                        selectedSpace?.syncteraAccountId,
                                });
                                router.navigate('/banking/transfer');
                            }}
                        />
                        <TileButton
                            icon={FileTextIcon}
                            text="Details"
                            onPress={() => {
                                router.navigate(
                                    '/banking/account-details-modal',
                                );
                            }}
                        />
                    </XStack>
                    {selectedSpace && (
                        <LegacyTransactions account={selectedSpace} />
                    )}
                </YStack>
            </YStack>
        </SafeAreaView>
    );
};

export default LegacySpaceDetails;
